import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useCompanyStore } from './company';

// A grant with companyId === null is universal (applies everywhere); with a
// number, it applies only to that one company - matches
// core360_purchaserequisition/backend's PermissionGrant (middleware/auth.ts).
export interface PermissionGrant {
  code: string;
  companyId: number | null;
}

export const useAuthStore = defineStore('auth', () => {
  const companyStore = useCompanyStore();
  const token = ref<string | null>(null);
  const name = ref('');
  const userName = ref('');
  const firstName = ref('');
  const lastName = ref('');
  const userEmail = ref('');
  const roles = ref<string[]>([]);
  const authInitError = ref<string | null>(null);

  // core360_auth permission grants (see its CORE360_ACCESS_BRIEF.md) - fetched
  // from this module's own GET /api/auth/me, same pattern as
  // core360_maintenance's stores/auth.ts. Kept as {code, companyId} pairs
  // (not flattened to bare code strings) so hasPermission can scope a check
  // to the currently-selected company - see hasPermission below.
  const permissions = ref<PermissionGrant[]>([]);
  // This module's own core360_auth module record - fetched once alongside
  // permissions so the app root can gate on it: universal modules skip
  // company selection/branding entirely.
  const moduleIsUniversal = ref(false);
  const moduleHasAccess = ref(false);
  const moduleAccessChecked = ref(false);

  const displayName = computed(() => {
    const full = [firstName.value, lastName.value].filter(Boolean).join(' ');
    return name.value || full || userName.value || userEmail.value;
  });

  // Backed by core360_auth permission codes (see its CORE360_ACCESS_BRIEF.md),
  // fetched into `permissions` below - not the pr-* Keycloak client roles this
  // used to read, which were never provisioned in the core360 realm and so
  // were permanently false. The matching backend checks live in
  // core360_purchaserequisition/backend's routes/pr.ts and requirePermission
  // (middleware/auth.ts). procurement.process_admin/view_expense_summary were
  // renamed to manage/view_expenses for naming consistency with manage_stock/
  // manage_bills - property names here kept as-is to avoid touching every
  // call site, only the underlying code strings changed.
  const isProcessAdmin = computed(() => hasPermission('procurement.manage'));
  const canManageBills = computed(() => hasPermission('procurement.manage_bills'));
  const canManageStock = computed(() => hasPermission('procurement.manage_stock'));
  const canViewExpenseSummary = computed(() => hasPermission('procurement.view_expenses'));
  // New: procurement.create previously had no matching backend enforcement
  // at all (see pr.ts's canCreate) - surfaced here so the UI can hide
  // "create a PR" entry points instead of letting someone reach a 403 with
  // no warning. view_all is read-only cross-user visibility, implied by
  // isProcessAdmin but grantable on its own too.
  const canCreatePr = computed(() => hasPermission('procurement.create'));
  const canViewAllPrs = computed(() => isProcessAdmin.value || hasPermission('procurement.view_all'));

  function setRoles(newRoles: string[]) {
    roles.value = newRoles;
  }

  function setAuthInitError(message: string | null) {
    authInitError.value = message;
  }

  function setToken(newToken: string | null) {
    token.value = newToken;
    if (newToken) {
      localStorage.setItem('authToken', newToken);
    } else {
      localStorage.removeItem('authToken');
    }
  }

  function setUserInfo(info: { name?: string; userName?: string; firstName?: string; lastName?: string; userEmail?: string }) {
    name.value = info.name ?? '';
    userName.value = info.userName ?? '';
    firstName.value = info.firstName ?? '';
    lastName.value = info.lastName ?? '';
    userEmail.value = info.userEmail ?? '';
  }

  // core360_landing hands the session over by opening this module with the
  // token in the URL hash (#t=<jwt>) - the hash is never sent to the server, so
  // it stays out of access logs. Read it, persist it, then strip it from the URL
  // so a copied link carries no credential and a reload doesn't re-apply a
  // stale one. Falls back to whatever is already in localStorage: that works
  // while every module is on localhost (storage is shared across ports) but not
  // once a module moves to its own hostname, which is why the hash exists.
  function consumeTokenFromHash() {
    const hash = window.location.hash;
    if (!hash.startsWith('#t=')) return false;

    const handed = decodeURIComponent(hash.slice(3));
    // Clear the hash regardless of whether the value is usable, so a malformed
    // one cannot linger in the address bar or survive a reload.
    history.replaceState(null, '', window.location.pathname + window.location.search);
    if (!handed) return false;

    setToken(handed);
    return true;
  }

  function restoreFromLocalStorage() {
    // Hash wins: it is the fresher token from the module the user just came from.
    if (consumeTokenFromHash()) return true;

    const stored = localStorage.getItem('authToken');
    if (stored) {
      token.value = stored;
      return true;
    }
    return false;
  }

  function logout() {
    setToken(null);
    permissions.value = [];
  }

  // True if the caller holds `code` either universally or specifically for
  // the currently-selected company - a grant scoped to one company doesn't
  // apply just because some OTHER company is selected. Mirrors the backend's
  // own hasPermission (middleware/auth.ts) so client-side gating and
  // server-side enforcement agree.
  function hasPermission(code: string): boolean {
    const companyId = companyStore.selectedCompanyId;
    return permissions.value.some(
      (g) => g.code === code && (g.companyId === null || (companyId !== null && g.companyId === companyId)),
    );
  }

  // GET /api/auth/me (this module's backend) - permission grants only
  // core360_auth knows about, resolved server-side per fetchPermissionGrants
  // in middleware/auth.ts.
  async function fetchPermissions(): Promise<void> {
    try {
      const res = await fetch('/api/auth/me', {
        headers: token.value ? { Authorization: `Bearer ${token.value}` } : {},
      });
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      const data = (await res.json()) as { permissions?: PermissionGrant[] };
      permissions.value = data.permissions ?? [];
    } catch (err) {
      console.error('[Auth] Failed to fetch permissions:', err);
      permissions.value = [];
    }
    await fetchModuleAccess();
  }

  // GET /api/auth/module-access (this module's backend, forwarding to
  // core360_auth's GET /api/modules/by-code/procurement).
  async function fetchModuleAccess(): Promise<void> {
    try {
      const res = await fetch('/api/auth/module-access', {
        headers: token.value ? { Authorization: `Bearer ${token.value}` } : {},
      });
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      const data = (await res.json()) as { isUniversal: boolean; hasAccess: boolean };
      moduleIsUniversal.value = data.isUniversal;
      moduleHasAccess.value = data.hasAccess;
    } catch (err) {
      console.error('[Auth] Failed to fetch module access:', err);
      moduleIsUniversal.value = false;
      moduleHasAccess.value = false;
    } finally {
      moduleAccessChecked.value = true;
    }
  }

  return {
    token,
    name,
    userName,
    firstName,
    lastName,
    userEmail,
    roles,
    authInitError,
    displayName,
    isProcessAdmin,
    canManageBills,
    canManageStock,
    canViewExpenseSummary,
    canCreatePr,
    canViewAllPrs,
    permissions,
    moduleIsUniversal,
    moduleHasAccess,
    moduleAccessChecked,
    setToken,
    setUserInfo,
    setRoles,
    setAuthInitError,
    restoreFromLocalStorage,
    consumeTokenFromHash,
    hasPermission,
    fetchPermissions,
    fetchModuleAccess,
    logout,
  };
});
